import React, { createContext, useContext, useReducer, ReactNode } from 'react'
import { Cart, CartItem, CartContextType } from '../types/cart'
import { OrderType } from '../types/order'

// Cart Actions
type CartAction =
  | { type: 'ADD_ITEM'; payload: Omit<CartItem, 'id'> & { id?: string } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'REPLACE_ITEM'; payload: { oldItemId: string; newItems: CartItem[] } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_ORDER_TYPE'; payload: OrderType }

// Helper function to calculate item total including surcharges (tax-inclusive for display)
const calculateItemTotal = (item: any): number => {
  const basePrice = item.price_cents * item.quantity
  const optionSurcharges = item.selected_options?.reduce(
    (sum: number, opt: any) => sum + (opt.price_delta_cents || 0), 0
  ) || 0
  return basePrice + (optionSurcharges * item.quantity)
}

// Cart Reducer
const cartReducer = (state: Cart, action: CartAction): Cart => {
  switch (action.type) {
    case 'ADD_ITEM': {
      // If an identical MAIN item exists (same variant and same options), merge quantities
      const isMain = action.payload.type === 'main'
      if (isMain) {
        const normalizeOptions = (opts: any[]) => JSON.stringify((opts || []).slice().sort((a, b) => a.option_item_id - b.option_item_id))
        const pv = Number((action.payload as any).product_variant_id)
        const targetKey = `${pv}|${normalizeOptions((action.payload as any).selected_options || [])}`
        const idx = state.items.findIndex(it => it.type === 'main' && Number(it.product_variant_id) === pv && normalizeOptions((it as any).selected_options || []) === targetKey.split('|')[1])
        if (idx !== -1) {
          const updated = [...state.items]
          const existing = updated[idx]
          // Don't merge if either item is a meal deal — they have distinct children
          if ((action.payload as any).is_meal_deal || (existing as any).is_meal_deal) {
            // Fall through to create a new separate item
          } else {
            const merged = { ...existing, quantity: existing.quantity + action.payload.quantity }
            updated[idx] = merged
            return {
              ...state,
              items: updated,
              total_cents: state.total_cents + calculateItemTotal(action.payload),
              // item_count counts only mains; adjust by added quantity
              item_count: state.item_count + action.payload.quantity,
            }
          }
        }
      }

      // For non-main items (bundle surcharge, meal parts), try to merge into existing child line under same parent
      if (!isMain) {
        const normalizeOptions = (opts: any[]) => JSON.stringify((opts || []).slice().sort((a, b) => a.option_item_id - b.option_item_id))
        const payload = action.payload as any
        const isBundle = payload.type === 'bundle'
        const childIdx = state.items.findIndex(it => {
          if (it.type !== payload.type) return false
          if ((it as any).bundle_parent_id !== payload.bundle_parent_id) return false
          if (!isBundle) {
            // meal_part should match group and variant
            if (Number((it as any).bundle_group_id) !== Number(payload.bundle_group_id)) return false
            if (Number(it.product_variant_id) !== Number(payload.product_variant_id)) return false
          }
          // options equality
          return normalizeOptions((it as any).selected_options || []) === normalizeOptions(payload.selected_options || [])
        })

        if (childIdx !== -1) {
          const updated = [...state.items]
          const existing = updated[childIdx]
          const merged = { ...existing, quantity: existing.quantity + payload.quantity }
          updated[childIdx] = merged
          return {
            ...state,
            items: updated,
            total_cents: state.total_cents + calculateItemTotal(payload),
            // item_count unchanged for non-main
            item_count: state.item_count,
          }
        }
      }

      const newItem: CartItem = {
        ...action.payload,
        product_variant_id: Number((action.payload as any).product_variant_id),
        id: (action.payload as any).id || `${Number((action.payload as any).product_variant_id)}-${Date.now()}-${Math.random()}`,
      }

      return {
        ...state,
        items: [...state.items, newItem],
        total_cents: state.total_cents + calculateItemTotal(newItem),
        // Count only main items
        item_count: state.item_count + (newItem.type === 'main' ? newItem.quantity : 0),
      }
    }
    
    case 'REMOVE_ITEM': {
      const itemToRemove = state.items.find(item => item.id === action.payload)
      if (!itemToRemove) return state

      // If removing a main item, also remove its related children (bundle surcharge and meal parts)
      let remainingItems = state.items.filter(item => item.id !== action.payload)
      let totalDiff = calculateItemTotal(itemToRemove)
      let itemCountDiff = (itemToRemove.type === 'main' ? itemToRemove.quantity : 0)
      if (itemToRemove.type === 'main') {
        const parentKey = itemToRemove.id
        const [childrenToRemove, keep] = [
          remainingItems.filter(it => it.bundle_parent_id === parentKey),
          remainingItems.filter(it => it.bundle_parent_id !== parentKey)
        ]
        remainingItems = keep
        childrenToRemove.forEach(child => {
          totalDiff += calculateItemTotal(child)
        })
      }

      return {
        ...state,
        items: remainingItems,
        total_cents: state.total_cents - totalDiff,
        item_count: state.item_count - itemCountDiff,
      }
    }
    
    case 'UPDATE_QUANTITY': {
      const { itemId, quantity } = action.payload
      const itemIndex = state.items.findIndex(item => item.id === itemId)
      if (itemIndex === -1) return state
      
      const item = state.items[itemIndex]
      const quantityDiff = quantity - item.quantity

      let updatedItems = [...state.items]
      if (quantity <= 0) {
        // Removing item; if it's a main, also remove its children
        if (item.type === 'main') {
          const parentKey = item.id
          const [childrenToRemove, keep] = [
            updatedItems.filter(it => it.bundle_parent_id === parentKey),
            updatedItems.filter(it => it.bundle_parent_id !== parentKey)
          ]
          updatedItems = keep
          // Remove the main as well
          updatedItems.splice(updatedItems.findIndex(it => it.id === item.id), 1)
          const childrenTotal = childrenToRemove.reduce((sum, c) => sum + calculateItemTotal(c), 0)
          return {
            ...state,
            items: updatedItems,
            total_cents: state.total_cents - calculateItemTotal(item) - childrenTotal,
            item_count: state.item_count - item.quantity,
          }
        } else {
          updatedItems.splice(itemIndex, 1)
          return {
            ...state,
            items: updatedItems,
            total_cents: state.total_cents - calculateItemTotal(item),
            item_count: state.item_count,
          }
        }
      } else {
        updatedItems[itemIndex] = { ...item, quantity }
        let totalIncrement = calculateItemTotal({ ...item, quantity: quantityDiff })
        let itemCountIncrement = (item.type === 'main' ? quantityDiff : 0)

        // If updating a main, propagate its quantity change to children with the same parent key
        if (item.type === 'main' && quantityDiff !== 0) {
          const parentKey = item.id
          updatedItems = updatedItems.map(it => {
            if (it.bundle_parent_id === parentKey) {
              return { ...it, quantity: it.quantity + quantityDiff }
            }
            return it
          })
          // Update total for children
          const children = state.items.filter(it => it.bundle_parent_id === parentKey)
          children.forEach(child => {
            totalIncrement += calculateItemTotal({ ...child, quantity: quantityDiff })
          })
        }

        return {
          ...state,
          items: updatedItems,
          total_cents: state.total_cents + totalIncrement,
          item_count: state.item_count + itemCountIncrement,
        }
      }
    }
    
    case 'REPLACE_ITEM': {
      const { oldItemId, newItems } = action.payload
      const oldItemIndex = state.items.findIndex(it => it.id === oldItemId)
      if (oldItemIndex === -1) return state

      const oldMain = state.items[oldItemIndex]
      const oldChildren = state.items.filter(it => it.bundle_parent_id === oldItemId)
      const oldTotal = calculateItemTotal(oldMain) + oldChildren.reduce((s, c) => s + calculateItemTotal(c), 0)
      const newTotal = newItems.reduce((s, it) => s + calculateItemTotal(it), 0)
      const newMainQuantity = newItems.find(it => it.type === 'main')?.quantity ?? 1

      // Remove old main and all its children, then insert new items at the same position
      const withoutOld = state.items.filter(it => it.id !== oldItemId && it.bundle_parent_id !== oldItemId)
      const updated = [
        ...withoutOld.slice(0, oldItemIndex),
        ...newItems,
        ...withoutOld.slice(oldItemIndex),
      ]

      return {
        ...state,
        items: updated,
        total_cents: state.total_cents - oldTotal + newTotal,
        item_count: state.item_count - oldMain.quantity + newMainQuantity,
      }
    }

    case 'CLEAR_CART':
      return {
        items: [],
        total_cents: 0,
        item_count: 0,
        order_type: state.order_type, // preserve order type across cart clears
      }

    case 'SET_ORDER_TYPE':
      return {
        ...state,
        order_type: action.payload,
      }

    default:
      return state
  }
}

// Initial State
const initialState: Cart = {
  items: [],
  total_cents: 0,
  item_count: 0,
  order_type: 'DINE_IN',
}

// Context
const CartContext = createContext<CartContextType | undefined>(undefined)

// Provider
interface CartProviderProps {
  children: ReactNode
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, initialState)

  const addItem = (item: Omit<CartItem, 'id'> & { id?: string }) => {
    dispatch({ type: 'ADD_ITEM', payload: item })
  }

  const removeItem = (itemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId })
  }

  const updateItemQuantity = (itemId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } })
  }

  const replaceItem = (oldItemId: string, newItems: CartItem[]) => {
    dispatch({ type: 'REPLACE_ITEM', payload: { oldItemId, newItems } })
  }

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' })
  }

  const getTotalPrice = () => {
    return cart.total_cents
  }

  const getItemCount = () => {
    return cart.item_count
  }

  const setOrderType = (type: OrderType) => {
    dispatch({ type: 'SET_ORDER_TYPE', payload: type })
  }

  const value: CartContextType = {
    cart,
    addItem,
    removeItem,
    updateItemQuantity,
    replaceItem,
    clearCart,
    getTotalPrice,
    getItemCount,
    setOrderType,
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}

// Hook
export const useCart = (): CartContextType => {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
